import classNames from "classnames";
import { LatLngExpression, SVG as LeafletSVG, svg } from "leaflet";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMap } from "react-leaflet";

type SensorMarkerKind = "dot" | "tag" | "missing";
type SensorSvgRenderer = LeafletSVG & { _rootGroup?: SVGGElement };

interface SensorMarkerLayerContextValue {
  root?: SVGGElement;
  moveRevision: number;
  registerPositionUpdater: (updater: () => void) => () => void;
}

const SensorMarkerLayerContext = createContext<SensorMarkerLayerContextValue>({
  moveRevision: 0,
  registerPositionUpdater: () => () => undefined,
});

interface SensorMarkerProps {
  position: LatLngExpression;
  sensorType: "precip" | "stream";
  kind: SensorMarkerKind;
  fillColor: string;
  color?: string;
  label?: string;
  radius?: number;
  fillOpacity?: number;
  strokeOpacity?: number;
  selected: boolean;
  paintOrder: number;
  onClick?: () => void;
}

const svgElement = <K extends keyof SVGElementTagNameMap>(tagName: K) =>
  document.createElementNS("http://www.w3.org/2000/svg", tagName);

const setAttributes = (
  element: SVGElement,
  attributes: Record<string, string | number>
) => {
  Object.entries(attributes).forEach(([name, value]) =>
    element.setAttribute(name, String(value))
  );
};

const warningOutlinePath =
  "M569.517 440.013C587.975 472.007 564.806 512 527.94 512H48.054c-36.937 0-59.999-40.055-41.577-71.987L246.423 23.985c18.467-32.009 64.72-31.951 83.154 0l239.94 416.028z";

export const SensorMarkerLayer = ({ children }: { children: ReactNode }) => {
  const map = useMap();
  const renderer = useMemo(
    () => svg({ pane: "markerPane" }) as SensorSvgRenderer,
    []
  );
  const [root, setRoot] = useState<SVGGElement>();
  const [moveRevision, setMoveRevision] = useState(0);
  const positionUpdaters = useRef(new Set<() => void>());
  const registerPositionUpdater = useCallback((updater: () => void) => {
    positionUpdaters.current.add(updater);
    return () => positionUpdaters.current.delete(updater);
  }, []);

  useEffect(() => {
    const updatePositions = () => {
      positionUpdaters.current.forEach((updatePosition) => updatePosition());
    };
    const refreshMarkers = () =>
      setMoveRevision((revision) => revision + 1);
    renderer.on("update", updatePositions);
    renderer.addTo(map);
    map.on("moveend", refreshMarkers);
    setRoot(renderer._rootGroup);
    return () => {
      renderer.off("update", updatePositions);
      map.off("moveend", refreshMarkers);
      renderer.removeFrom(map);
    };
  }, [map, renderer]);

  return (
    <SensorMarkerLayerContext.Provider
      value={{ root, moveRevision, registerPositionUpdater }}
    >
      {children}
    </SensorMarkerLayerContext.Provider>
  );
};

const SensorMarker = ({
  position,
  sensorType,
  kind,
  fillColor,
  color = "black",
  label = "",
  radius = 7,
  fillOpacity = 1,
  strokeOpacity = 1,
  selected,
  paintOrder,
  onClick,
}: SensorMarkerProps) => {
  const map = useMap();
  const { root, registerPositionUpdater } = useContext(
    SensorMarkerLayerContext
  );
  const mapBounds = map.getBounds();
  const shouldRender = mapBounds.pad(0.25).contains(position);
  const isInMapView = mapBounds.contains(position);
  const width =
    kind === "tag"
      ? Math.max(20, label.length * 7.5 + 8)
      : kind === "missing"
      ? (radius * 9) / 4
      : radius * 2;
  const height = kind === "tag" ? 16 : radius * 2;
  const className = classNames(
    "leaflet-interactive",
    "selectable-marker",
    "sensor-marker",
    `${sensorType}-marker`,
    `${kind}-marker`,
    kind === "missing" ? "is-missing" : "is-reporting",
    {
      "is-selected": selected,
      "is-in-map-view": isInMapView,
    }
  );
  const stroke = selected ? "red" : "black";
  const strokeWidth = selected ? 2.5 : 1;
  const group = useMemo(() => svgElement("g"), []);

  useLayoutEffect(() => {
    if (!root || !shouldRender) {
      return;
    }
    group.dataset.markerOrder = String(paintOrder);
    const nextGroup = Array.from(root.children).find(
      (child) => Number((child as SVGGElement).dataset.markerOrder) > paintOrder
    );
    root.insertBefore(group, nextGroup || null);
    return () => {
      group.remove();
    };
  }, [group, paintOrder, root, shouldRender]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const center = map.latLngToLayerPoint(position);
      group.setAttribute(
        "transform",
        `translate(${center.x - width / 2} ${center.y - height / 2})`
      );
      group.classList.toggle(
        "is-in-map-view",
        map.getBounds().contains(position)
      );
    };
    updatePosition();
    return registerPositionUpdater(updatePosition);
  }, [group, height, map, position, registerPositionUpdater, width]);

  useLayoutEffect(() => {
    setAttributes(group, {
      class: className,
      "pointer-events": "all",
    });
    group.replaceChildren();

    if (kind === "dot") {
      const circle = svgElement("path");
      setAttributes(circle, {
        class: "leaflet-interactive",
        d: `M ${width / 2 - radius} ${height / 2} a ${radius} ${radius} 0 1 0 ${
          radius * 2
        } 0 a ${radius} ${radius} 0 1 0 ${-radius * 2} 0`,
        fill: fillColor,
        "fill-opacity": fillOpacity,
        stroke,
        "stroke-opacity": selected ? 1 : strokeOpacity,
        "stroke-width": strokeWidth,
      });
      group.appendChild(circle);
    } else if (kind === "tag") {
      const rectangle = svgElement("path");
      setAttributes(rectangle, {
        class: "leaflet-interactive",
        d: `M 4 0 H ${width - 4} Q ${width} 0 ${width} 4 V ${
          height - 4
        } Q ${width} ${height} ${width - 4} ${height} H 4 Q 0 ${height} 0 ${
          height - 4
        } V 4 Q 0 0 4 0 Z`,
        fill: fillColor,
        "fill-opacity": fillOpacity,
        stroke,
        "stroke-opacity": selected ? 1 : strokeOpacity,
        "stroke-width": strokeWidth,
      });
      group.appendChild(rectangle);

      const text = svgElement("text");
      setAttributes(text, {
        "pointer-events": "none",
        x: width / 2,
        y: height / 2,
        fill: color,
        "dominant-baseline": "central",
        "text-anchor": "middle",
        "font-size": 14,
        "font-weight": "bold",
      });
      text.textContent = label;
      group.appendChild(text);
    } else {
      if (selected) {
        const selectionOutline = svgElement("path");
        setAttributes(selectionOutline, {
          d: warningOutlinePath,
          transform: `scale(${width / 576} ${height / 512})`,
          fill: "none",
          stroke: "red",
          "stroke-width": strokeWidth * (576 / width),
          "stroke-linejoin": "round",
        });
        group.appendChild(selectionOutline);
      }

      const warning = svgElement("path");
      setAttributes(warning, {
        class: "leaflet-interactive",
        d: warningOutlinePath,
        transform: `scale(${width / 576} ${height / 512})`,
        fill: fillColor,
        "fill-opacity": selected ? 1 : fillOpacity,
        stroke: "black",
        "stroke-opacity": strokeOpacity,
        "stroke-width": 576 / width,
        "stroke-linejoin": "round",
      });
      group.appendChild(warning);

      const warningStem = svgElement("rect");
      setAttributes(warningStem, {
        x: 238,
        y: 155,
        width: 100,
        height: 205,
        rx: 35,
        transform: `scale(${width / 576} ${height / 512})`,
        fill: "white",
      });
      group.appendChild(warningStem);

      const warningDot = svgElement("circle");
      setAttributes(warningDot, {
        cx: 288,
        cy: 418,
        r: 48,
        transform: `scale(${width / 576} ${height / 512})`,
        fill: "white",
      });
      group.appendChild(warningDot);
    }
  }, [
    className,
    color,
    fillColor,
    fillOpacity,
    group,
    height,
    kind,
    label,
    radius,
    selected,
    stroke,
    strokeOpacity,
    strokeWidth,
    width,
  ]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      event.stopPropagation();
      onClick?.();
    };
    group.addEventListener("click", handleClick);
    return () => {
      group.removeEventListener("click", handleClick);
    };
  }, [group, onClick]);

  return null;
};

export default SensorMarker;